import { Injectable } from '@angular/core'
import { PERMISSIONS, PhdsoftCommonService, SESSION_STORAGE, User } from '@phdsoft/common'
import { StorageService } from '@phdsoft/common'
import { Util } from '@phdsoft/common'
import { environment } from 'src/environments/environment'
import { AssetInfoModel } from 'projects/phdsoft/common/src/lib/models/asset-info.model'
import { ReferenceDateRequest } from 'projects/phdsoft/common/src/lib/models/referencedate-request.model'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Asset } from 'src/app/platform/busines-data/Framework/Asset'
import { CollectionService } from 'src/app/collections/collection.service'
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model'
import { Observable } from 'rxjs'
import { DegradationCurveParameters } from 'src/app/degradation/models/degradation-curve'
import { CriticalAreaService } from 'src/app/template/critical-area-window/critical-area.service'
import { DegradationParametersLoaderService } from 'src/app/degradation/degradation-parameters-loader.service'
import { timeout, tap } from 'rxjs/operators'
import { RecompartmentElementResult } from 'src/app/asset-manager/recompartment/recompartment-result'

interface GeometryCurveParametersRequest {
  AssetId: string
  GeometryId: string
  RegionIds: string[]
  Date: Date
  InspectionTypeIds?: string[]
  MeasurementName?: string
  SectionId?: string
  Timestamp?: number
}

export interface RecompartmentElementRequest {
  AssetId: string
  ElementId: string
  SectionId: string
  OldRegionIds: string[]
  NewRegionIds: string[]
  ElementProperty: any
  ChangeType: boolean
  // The backend stores the typed Name as the region Description and ignores a
  // separate free-text description (decision D1, 2026-10-01), so it is no longer sent.
  // NewRegions?: Array<{ Id: string; Name: string; Description: string }>
  NewRegions?: Array<{ Id: string; Name: string; Description?: string }>
  NewSectionId?: string | null
  NewSections?: Array<{ Id: string; Name: string; CloneFromSectionId: string | null }>
}

// Body of POST Inspection/ReApplyElementsFromAssetScoped (backend ReapplyAssetRequestDTO).
// regionIds: null or [] = whole asset; sectionIds: null or [] = no section filter;
// elementsFile: server-side path under the API's Reapply:ElementsFileRoot, null = reapply from the database;
// allowRegionMoves: enables the cross-region / proximity retries that may move an element to another region.
export interface ReapplyAssetRequest {
  assetId: string
  regionIds: string[] | null
  sectionIds: string[] | null
  elementsFile: string | null
  allowRegionMoves: boolean
}

export interface ReapplyStartedResponse {
  assetId: string
  message: string
}

@Injectable({ providedIn: 'root' })
export class DigitalTwinService {
  public fullAsset: any = []
  public assetId: string
  public userId: string

  public constructor(
    private phdsoftCommonService: PhdsoftCommonService,
    private criticalAreaService: CriticalAreaService,
    private storageService: StorageService,
    private http: HttpClient,
    private collectionService: CollectionService,
    private degradationParametersLoader: DegradationParametersLoaderService
  ) { }

  getAsset(): Promise<AssetInfoModel> {
    return this.http.get<AssetInfoModel>(
      environment.API_THREED + `AssetInfo/secureurl/${this.assetId}`
    ).toPromise();
  }

  getAssetById(assetId: string): Promise<AssetInfoModel> {
    return this.http.get<AssetInfoModel>(
      environment.API_THREED + `AssetInfo/secureurl/${assetId}`
    ).toPromise();
  }

  async getUrls(): Promise<any[]> {
    const _RESPONSE = await this.http.get<any[]>(
      environment.API_THREED +
      `AssetGeometry/secureurl/${this.assetId}`
    ).toPromise();
    const responseResult: boolean = _RESPONSE !== null && _RESPONSE !== undefined
    let urls = []
    if (responseResult) {
      urls = _RESPONSE
    }
    return urls
  }

  async loadElementsProperties(asset: Asset, propertyPath: string, regionId: string): Promise<void> {
    const offline = !window.navigator.onLine
    let _property: any = await this.phdsoftCommonService.getFileArrayBuffer(propertyPath, offline)
    _property = JSON.parse(new TextDecoder().decode(_property))
    if (_property) {
      if (_property.body) {
        _property = Util.ab2json(_property.body)
      }
      if (_property.ElementProperties) {
        asset.elementsProperties = _property.ElementProperties;
        asset.setElementsProperties(regionId, _property.ElementProperties);
      } else if (_property.length && _property.length > 0) {
        asset.elementsProperties = _property;
        asset.setElementsProperties(regionId, _property);
      }
    }
    return void 0
  }

  async loadProperties(asset: Asset, propertyPath: Array<string>, regionId: string): Promise<void> {
    const forLoop = async () => {
      console.log('🛰 START loadElementsPropertiesForRegion')

      for (const path of propertyPath) {
        await this.loadElementsProperties(asset, path, regionId)
        console.log('🛰 Each loadElementsPropertiesForRegion')
      }

      console.log('🛰 END loadElementsPropertiesForRegion')
    }

    await forLoop()
  }

  estimateDegradationOnDate(assetId, regions, date) {
    const request: ReferenceDateRequest = {
      AssetId: assetId,
      RegionIds: regions,
      Date: date,
    }
    return this.http.post<void>(
      environment.API_DEGRAD +
      `Degradation/EstimateDegradation`,
      request
    )
  }

  getAllDegradationCurveParameters(assetId: string, regions: string[], date?: Date): Observable<DegradationCurveParameters[]>{
    var request: ReferenceDateRequest = {
      AssetId: assetId,
      RegionIds: regions,
    }

    if(date) request.Date = date

    // Set loading state to false for all regions (indicating loading started)
    regions.forEach(regionId => {
      this.degradationParametersLoader.setDegradationParametersLoaded(regionId, false);
    });

    var result =  this.http.post<DegradationCurveParameters[]>(
      environment.API_DEGRAD +
      `Degradation/GetAllCurveParameters`,
      request
    )
    .pipe(
      timeout(300000), // 300000 ms = 300 seconds
      tap(() => {
        // Set loading state to true for all regions when request completes successfully
        regions.forEach(regionId => {
          this.degradationParametersLoader.setDegradationParametersLoaded(regionId, true);
        });
      })
    );

    return result
  }

  getAverageThicknessFromLatestMeasurementsByGeometry(assetId: string, regionId: string, date: Date): Observable<Record<string, number>>{
    const params = {
      assetId: assetId,
      regionId: regionId,
      date: date.toISOString()
    }

    return this.http.get<Record<string, number>>(
      environment.API_INSPEC + `Inspection/GetAverageThicknessFromLatestMeasurementsByGeometry`,
      { params }
    )
  }

  getDegradationCurveParameters(assetId: string, regions: string[], inspectionElementId:string, parentGeometryId:string, referenceDate: Date): Observable<DegradationCurveParameters>{
    const request: any = {
      AssetId: assetId,
      RegionIds: regions,
      ParentGeometryId: parentGeometryId,
      InspectionElementId: inspectionElementId,
      Date: referenceDate
    }

    // ATTENTION: this field is not used by the backend, HOWEVER, it allows quick refresh of the degradation curve
    // Otherwise, some internal cache from http client or the gateway might block identical requests in a very short period of time
    // The timestamp differentiates these requests and allows them to proceed!
    request.Timestamp = new Date().getTime()

    var result =  this.http.post<DegradationCurveParameters>(
      environment.API_DEGRAD +
      `Degradation/GetCurveParameters`,
      request,
    )
    return result
  }

  getDegradationCurveParametersForGeometry(assetId: string, geometryId: string, regions: string[], referenceDate: Date, inspectionTypeIds?: string[], measurementName?: string, sectionId?: string): Observable<DegradationCurveParameters[]>{
    const request: GeometryCurveParametersRequest = {
      AssetId: assetId,
      GeometryId: geometryId,
      RegionIds: regions,
      Date: referenceDate,
      InspectionTypeIds: inspectionTypeIds,
      MeasurementName: measurementName,
      SectionId: sectionId,
      Timestamp: new Date().getTime()
    }

    var result =  this.http.post<DegradationCurveParameters[]>(
      environment.API_DEGRAD +
      `Degradation/GetCurveParametersForGeometry`,
      request,
    )
    return result
  }

  async droneImageTest(fileList: any[], assetId: string) {
    let formData: FormData = new FormData();
    formData.append('assetId', JSON.stringify(assetId));
    Array.from(fileList).forEach(file => {
      formData.append('pFormFiles', file);
    });

    let _response = await this.http.post<any>(environment.API_INSPEC + `DroneImage/processimage`, formData).toPromise();

    console.log(_response)
    return _response
  }

  async localToGlobalCoordinates(localCoordinates: any[], assetId: string) {
    // Convert array of coordinate arrays to CoordinateViewModel objects
    const coordinates = localCoordinates.map(point => {
      return {
        X: point[0],
        Y: point[1],
        Z: point[2]
      };
    });

    // Create a request matching controller's expected format:
    // 1. localCoordinates as the request body (application/json)
    // 2. assetId as a JSON string in the query parameter (required by JsonModelBinder)
    const headers = { 'Content-Type': 'application/json' };
    try {
      // The JsonModelBinder expects a JSON string, so we stringify the assetId
      let _response = await this.http.post<any>(
        `${environment.API_INSPEC}DroneImage/transformcoordinatesLocalToGlobal?assetId=${JSON.stringify(assetId)}`,
        coordinates,
        { headers }
      ).toPromise();
      
      // console.log('Response:', _response);
      return _response;
    } catch (error) {
      console.error('Error in localToGlobalCoordinates:', error);
      throw error;
    }
  }
  async globalToLocalCoordinates(globalCoordinates: any[], assetId: string) {
    // Convert array of coordinate arrays to CoordinateViewModel objects
    const coordinates = globalCoordinates.map(point => {
      return {
        X: point[0],
        Y: point[1],
        Z: point[2]
      };
    });

    // Create a request matching controller's expected format:
    // 1. localCoordinates as the request body (application/json)
    // 2. assetId as a JSON string in the query parameter (required by JsonModelBinder)
    const headers = { 'Content-Type': 'application/json' };
    try {
      // The JsonModelBinder expects a JSON string, so we stringify the assetId
      let _response = await this.http.post<any>(
        `${environment.API_INSPEC}DroneImage/transformcoordinatesGlobalToLocal?assetId=${JSON.stringify(assetId)}`,
        coordinates,
        { headers }
      ).toPromise();
      
      // console.log('Response:', _response);
      return _response;
    } catch (error) {
      console.error('Error in localToGlobalCoordinates:', error);
      throw error;
    }
  }

  async GenerateSpreadSheet(assetId: string, filterId: any, planId: any, formData?: any, sectionIds?:any) {
    {
      const user = await this.storageService.getItem<User>(
        SESSION_STORAGE.USER,
      )
      let data = {
        "Author": formData?.author,
        "Company": formData?.company,
        "Label": "",
        "AssetId": assetId,
        "FilterId": filterId,
        "PlanId": planId,
        "ViewType": formData.viewType,
        "ForceSplit": formData?.forceSplit ?? false,
        "IncludeTypicalDetails": formData?.includeTypicalDetails ?? true,
        "CreatedBy": "",
        "CreatedById": "96eaf4c6390f4339a81b16892660683d",
        "CreatedAt": "2020-09-17T00:00:00Z",
        "CustomerId": "371cd73be9d34ed49535c60a8e166384",
        "CustomerAlias": "phdsoft",
        "LimitDate": "2021-05-22T00:00:00Z",
        // "CollectionIds": [collections[0]],
        // "TypeNames": [typeNames[0]],
        "UserId": user.id,
        "SectionsIds":user.PermissionIds.includes(PERMISSIONS.UTILITIES)?sectionIds:null
      }
      let _response = await this.http.post<any>(environment.API_INSPEC + `Spreadsheet/publishinspectionplan`, data).toPromise();
    }
  }

  postJsonFilesToGltf(files: File[], assetId: string, collectionId: string, userId: string) {
    let formData = new FormData();
    for (let index = 0; index < files.length; index++) {
      let element = files[index];
      formData.append("pFormFiles", element)
    }

    formData.append("assetId", assetId);
    formData.append("collectionId", collectionId);
    formData.append("userId", userId);
    // formData.append("collectionId", uuid().replace(/-/g, '') as string);
    //formData.append("collectionId", "f55780476c05499783453f1a1c9937ca");

    return this.http.post(
      environment.GLTF_CONVERTER, formData, { responseType: 'text' }
      // 'http://localhost:3000/gltf', formData, { responseType: 'text' }
      // 'https://phdsoft-gltfconverter.azurewebsites.net/', formData
    )
  }

  postLightModelToGltf(files: File[], assetId: string, userId: string) {
    let formData = new FormData();
    for (let index = 0; index < files.length; index++) {
      let element = files[index];
      formData.append("pFormFiles", element)
    }

    formData.append("assetId", assetId);
    formData.append("userId", userId);
    formData.append("lightModel", "true");
    formData.append("dracoCompress", "false");

    return this.http.post(
      environment.GLTF_CONVERTER, formData, { responseType: 'text' }
    )
  }

  async updateProperties(properties: any, regionId: string, assetId: string, changeType: boolean = false,
    changeGeometry:boolean = false, WebGeometryV:any = null, FlangeGeometryV:any = null, newElement:any = null, NewGeometryL:any = null,
    deleteGeometry:boolean = false, idToDelete:string = null, sectionId:string = null) {
    let _data = {
      AssetId: assetId,
      RegionId: regionId,
      SectionId: properties? properties.SectionId:newElement?.SectionId??sectionId,
      ElementId: properties?.ElementId,
      ElementProperty: properties,
      ChangeType: changeType,
      DeleteGeometry: deleteGeometry,
      IdToDelete: idToDelete,
      ChangeGeometry: changeGeometry,
      WebGeometryV: WebGeometryV,
      FlangeGeometryV: FlangeGeometryV,
      NewGeometryL: NewGeometryL,
      NewElement: newElement
    };

    return await this.http.post<void>(
      environment.API_THREED + `AssetInfo/UpdateElementProperty`,
      _data,
    )
  }

  async updatePropertiesList(properties: any, regionId: string, assetId: string) {
    let _data = {
      AssetId: assetId,
      RegionId: regionId,
      SectionId: properties[0].SectionId,
      // ElementId: properties[0].ElementId,
      ElementProperties: properties
    };

    return await this.http.post<void>(
      environment.API_THREED + `AssetInfo/UpdateElementPropertyList`,
      _data,
    ).toPromise()
  }

  async changeInspectionScope(assetId:string, targetId: any, scope: string):Promise<any> {
    return await this.http.post<void>(
      environment.API_INSPEC + `Inspection/ChangeElementTypeScope?assetId=${assetId}&targetId=${targetId}&scope=${scope}`,
      null,
    )
  }

  /**
   * Moves a structural element to a new region set on the ThreeD service
   * (rewrites the per-region properties/geometry files and regenerates the GLTFs).
   */
  recompartmentElement(request: RecompartmentElementRequest): Observable<RecompartmentElementResult> {
    return this.http.post<RecompartmentElementResult>(environment.API_THREED + 'AssetInfo/RecompartmentElement', request)
  }

  /** Reassigns the inspections (and their anomalies) targeting the element to the new region set. */
  recompartmentInspections(assetId: string, targetId: string, regionIds: string[], regionNames: string[],
      sectionId: string | null = null, sectionName: string | null = null): Observable<any> {
    return this.http.post<any>(
      environment.API_INSPEC + `Inspection/RecompartmentElement?assetId=${assetId}&targetId=${targetId}`,
      { RegionIds: regionIds, RegionNames: regionNames, SectionId: sectionId, SectionName: sectionName },
    )
  }

  async updateGeometryJson(formData: any) {
    return await this.http.post(
      environment.API_THREED + 'AssetFiles/AddGeometriesToCollectionWithIds', formData
    ).toPromise()
  }

  async updateLinesGeometry(linesToUpdate: any) {
    return await this.http.post<void>(
      environment.API_THREED + `AssetInfo/UpdateLineGeometry`,
      linesToUpdate,
    ).toPromise()
  }

  async addLinesGeometry(linesToUpdate: any, assetId: string) {
    linesToUpdate.AssetId = assetId;
    return await this.http.post<void>(
      environment.API_THREED + `AssetInfo/AddReferenceLineGeometry`,
      linesToUpdate,
    ).toPromise()
  }

  async updateElementsIdsGeometry(elementsIdsToUpdate: any, assetId: string) {
    // let _data = {
    //   AssetId: this.graphics.assetId, RegionId: region,
    //   SectionId: elementProperty.SectionId, ElementId: elementProperty.ElementId, ElementProperty: elementProperty
    // };
    elementsIdsToUpdate.AssetId = assetId;
    return await this.http.post<void>(
      environment.API_THREED + `AssetInfo/UpdateElementIdsGeometry`,
      elementsIdsToUpdate
    ).toPromise()
  }

  //TODO
  async getCriticalAreaElements(regionId: string, assetId: string) {
    this.collectionService.setInspectionLoadStatus(true)
    const _RESPONSE = await this.criticalAreaService.getCriticalAreas(regionId, assetId).toPromise()
    this.collectionService.setInspectionLoadStatus(false)
    return _RESPONSE
  }

  getInspectionsByFilters(filterId: string): Observable<ElementResultInfo[]> {
    return this.http.get<ElementResultInfo[]>(
      environment.API_INSPEC + `Inspection/GetByFilters/${filterId}`
    )
  }

  getInspectionsByPlans(planId: string): Observable<ElementResultInfo[]> {
    return this.http.get<ElementResultInfo[]>(
      environment.API_INSPEC + `Inspection/GetByPlans/${planId}`
    )
  }

  // Distinct ScoutImportId values already imported for this asset (optionally scoped to a Scout
  // session code). Used by the drone-session import form to exclude already-imported UTM/POI points.
  getImportedScoutIds(assetId: string, scoutSessionCode?: string): Observable<string[]> {
    const sessionParam = scoutSessionCode ? `&scoutSessionCode=${scoutSessionCode}` : ''
    return this.http.get<string[]>(
      environment.API_INSPEC + `Inspection/GetImportedScoutIds?assetId=${assetId}${sessionParam}`
    )
  }

  async deleteInspectionsByFilters(filterId: string): Promise<ElementResultInfo[]> {
    const _RESPONSE: ElementResultInfo[] = await this.http.get<ElementResultInfo[]>(
      environment.API_INSPEC + `Inspection/DeleteByFilters/${filterId}`
    ).toPromise();
    return _RESPONSE
  }

  async reapplyInspectionsByFilters(filterId: string, sectionIds:any): Promise<ElementResultInfo[]> {
    const _RESPONSE = await this.http.post<any>(
      environment.API_INSPEC + `Inspection/ReApplyElements`, sectionIds,
      {
        params: {
          filterId: filterId
        }
      }
      // environment.API_INSPEC + `Inspection/.ts?filterId=${filterId}&lockedStatus=${lockedStatus}`,

    ).toPromise();
    return _RESPONSE
  }

  // The server validates the request, starts the reapply in the background and answers 202 at once
  // (409 while the same asset is still running); progress and completion arrive as SignalR notifications.
  reapplyInspectionsFromAssetScoped(request: ReapplyAssetRequest): Observable<ReapplyStartedResponse> {
    return this.http.post<ReapplyStartedResponse>(
      environment.API_INSPEC + `Inspection/ReApplyElementsFromAssetScoped`,
      request,
    )
  }

  async FixScopeElementsInspectionsByFilters(filterId: string): Promise<ElementResultInfo[]> {
    const _RESPONSE = await this.http.post<any>(
      environment.API_INSPEC + `Inspection/FixScopeElements?filterId=${filterId}`, filterId
    ).toPromise();
    return _RESPONSE
  }

  async equalizeColinearLabels(assetId: string, regionsIds: Array<string>, sectionIds:Array<string>): Promise<any> {
    let ids = {Item1:regionsIds, Item2:sectionIds}
    const _RESPONSE = await this.http.post<any>(
      environment.API_THREED + `AssetInfo/UpdateSameLineLabels?assetId=${assetId}`, ids,
    ).toPromise();
    return _RESPONSE
  }
  async reapplyInspectionsAfterMergeAndCut(assetId: string, targetsIds:any, startDate: Date, regionId: string): Promise<Observable<ElementResultInfo[]>> {
    return await this.http.post<any>(
      environment.API_INSPEC + `Inspection/ReApplyCutMerge`, targetsIds,
      {
        params: {
          assetId: assetId,
          startDate: startDate.toISOString(),
          regionId: regionId
        }
      }
      // environment.API_INSPEC + `Inspection/.ts?filterId=${filterId}&lockedStatus=${lockedStatus}`,

    )
  }

  updateElementVisual(assetId: string, regionId: string, sectionId: string, elementId: string, visual: any) {
    return this.http.post<void>(
      environment.API_THREED + `AssetInfo/UpdateElementVisual/`,
      { AssetId: assetId, RegionId: regionId, SectionId: sectionId, ElementId: elementId, Visual: visual },
    )
  }

  updateElementVisualList(assetId: string, elementVisuals: Array<{
    RegionId: string; SectionId: string; ElementId: string; Visual: any;
  }>) {
    return this.http.post<void>(
      environment.API_THREED + `AssetInfo/UpdateElementVisualList/`,
      { AssetId: assetId, ElementVisuals: elementVisuals },
    )
  }

  /** Regenerates the GLTF of the given regions; resolves to false when any region failed. */
  generateGltf(assetId: string, regionIds: string[]) {
    return this.http.post<boolean>(
      environment.API_THREED + `StructuralCollection/GenerateGltf/${assetId}`,
      regionIds,
    )
  }

  computeTypicalDetailsForAsset(assetId: string): Observable<any> {
    return this.http.post<any>(
      environment.API_THREED + `TypicalDetails/ComputeForAsset?assetId=${assetId}`,
      null,
    )
  }
}
